import { useState, useEffect } from 'react';
import { EmojiType } from '@/types/post';
import { usePosts } from '@/contexts/PostsContext';
import { useLanguage } from '@/contexts/LanguageContext';

const EMOJIS: { emoji: EmojiType; label: string }[] = [
  { emoji: '❤️', label: 'Love' },
  { emoji: '😮', label: 'Wow' },
  { emoji: '😢', label: 'Sad' },
  { emoji: '😄', label: 'Happy' },
  { emoji: '🤔', label: 'Thinking' },
  { emoji: '🔥', label: 'Fire' },
  { emoji: '✊', label: 'Power' },
];

function getFingerprint(): string {
  let fp = localStorage.getItem('reaction_fp');
  if (!fp) {
    fp = crypto.randomUUID();
    localStorage.setItem('reaction_fp', fp);
  }
  return fp;
}

interface EmojiReactionProps {
  postId: string;
  reactions: Record<string, number>;
}

const EmojiReaction = ({ postId, reactions }: EmojiReactionProps) => {
  const { reactToPost } = usePosts();
  const { t } = useLanguage();
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState('');

  const [userReaction, setUserReaction] = useState<string | null>(() => {
    const key = `react_${postId}`;
    const saved = localStorage.getItem(key);
    return saved && EMOJIS.some((e) => e.emoji === saved) ? saved : null;
  });

  // Sync userReaction when localStorage/external changes occur
  useEffect(() => {
    const key = `react_${postId}`;
    const saved = localStorage.getItem(key);
    if (saved && saved !== userReaction) {
      setUserReaction(saved);
    }
  }, [postId, userReaction]);

  const totalReactions = Object.values(reactions).reduce((a, b) => a + b, 0);

  const handleReact = async (emoji: EmojiType) => {
    setError('');
    const fp = getFingerprint();
    const key = `react_${postId}`;
    const currentSaved = localStorage.getItem(key);

    // Optimistic update
    if (currentSaved === emoji) {
      // Toggle off: decrement count
      localStorage.removeItem(key);
      setUserReaction(null);
    } else {
      // Toggle on (or switch)
      localStorage.setItem(key, emoji);
      setUserReaction(emoji);
    }

    try {
      await reactToPost(postId, emoji, fp);
      // Server will return correct counts, already handled in context
    } catch {
      // On failure, revert localStorage to previous state
      if (currentSaved) {
        localStorage.setItem(key, currentSaved);
        setUserReaction(currentSaved);
      } else {
        localStorage.removeItem(key);
        setUserReaction(null);
      }
      setError(t('Failed to save reaction. Please try again.', '반응 저장에 실패했습니다. 다시 시도해주세요.'));
    }
    setShowAll(false);
  };

  const displayedEmojis = EMOJIS.filter(({ emoji }) => reactions[emoji] > 0)
    .sort((a, b) => reactions[b.emoji] - reactions[a.emoji]);

  return (
    <div className="flex flex-col gap-2">
      {error && (
        <p className="text-xs text-red-500 bg-red-50 rounded-lg px-3 py-1.5">{error}</p>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {displayedEmojis.map(({ emoji }) => (
          <div
            key={emoji}
            className={`flex items-center gap-1 border rounded-full px-3 py-1 ${
              userReaction === emoji
                ? 'bg-stone-800 text-white border-stone-800'
                : 'bg-stone-50 border-stone-100'
            }`}
          >
            <span className="text-base">{emoji}</span>
            <span className={`text-xs font-medium ${userReaction === emoji ? 'text-white' : 'text-stone-500'}`}>
              {reactions[emoji]}
            </span>
          </div>
        ))}
        <span className="text-xs text-stone-400 ml-1">{totalReactions} reactions</span>
      </div>

      <div className="relative">
        <button
          onClick={() => setShowAll((v) => !v)}
          className={`text-xs px-4 py-2 rounded-full border transition-all whitespace-nowrap cursor-pointer font-medium ${
            userReaction
              ? 'bg-stone-800 text-white border-stone-800'
              : 'bg-white text-stone-600 border-stone-200 hover:border-stone-400'
          }`}
        >
          {userReaction ? `You reacted ${userReaction}` : 'React'}
        </button>

        {showAll && (
          <div className="absolute bottom-full left-0 mb-2 flex items-center gap-1 bg-white border border-stone-100 rounded-2xl px-3 py-2 shadow-sm z-10">
            {EMOJIS.map(({ emoji, label }) => (
              <button
                key={emoji}
                title={label}
                onClick={() => handleReact(emoji)}
                className={`text-xl transition-transform hover:scale-125 cursor-pointer ${
                  userReaction === emoji ? 'scale-125 opacity-100' : 'opacity-70 hover:opacity-100'
                }`}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmojiReaction;